import type { DepositAccount } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Keyboard, Pressable } from 'react-native';
import Animated from 'react-native-reanimated';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledTextInput,
    Form,
    isNonMobileWeb,
    redactPrivate,
    ScreenContainer,
    StyledH1EmphasizedInput,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import ControlledDepositAccountSelect from '../../../common/components/controlled/ControlledDepositAccountSelect';
import {
    formatCurrencyInputOnChange,
    formattedDollarsToCents,
    MINIMUM_TRANSFER_AMOUNT_CENTS,
} from '../../../common/utils/bankingUtils';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import type { CheckDepositFormData } from '../../types/CheckDeposit';
import { NeedsReviewSheet } from '../transfers/NeedsReview';
import SelectAccountSheet from '../transfers/SelectAccountSheet';
import { CheckDepositLimitsButton } from './CheckDepositLimitsSheet';

const depositAccountSchema = z.object({
    id: z.string(),
    accountType: z.string(),
    nickname: z.string(),
    bankName: z.string(),
    accountNumberMasked: z.string(),
    accountNumber: z.string().nullish(),
    accountNumberMaskedIsTokenized: z.boolean(),
    isExternal: z.boolean(),
    imgBase64: z.string().optional(),
    logo: z.string().optional(),
    icon: z.string().optional(),
    color: z.string().optional(),
    verified: z.boolean().optional(),
});

const checkDepositSchema = z
    .object({
        amount: z
            .string({ required_error: 'Please enter the check amount' })
            .min(1, 'Please enter the check amount'),
        depositAccount: depositAccountSchema.nullable(),
    })
    .refine(({ depositAccount }) => depositAccount != null, {
        message: 'Please select a deposit account',
        path: ['depositAccount'],
    })
    .refine(
        ({ amount }) =>
            formattedDollarsToCents(amount) >= MINIMUM_TRANSFER_AMOUNT_CENTS,
        { message: 'Amount must be at least $0.01', path: ['amount'] },
    );

type CheckDepositSchema = z.infer<typeof checkDepositSchema>;

const FORM_MAX_WIDTH = 500;

interface CheckDepositDetailsProps {
    onSubmit: (data: CheckDepositFormData) => void;
}

const CheckDepositDetails = ({
    onSubmit: onSubmitProp,
}: CheckDepositDetailsProps) => {
    const [isAccountSheetOpen, setIsAccountSheetOpen] = useState(false);
    const [isReviewSheetOpen, setIsReviewSheetOpen] = useState(false);

    const { data: depositAccounts = [] } =
        useGetInternalAndExternalDepositAccounts(undefined, {
            select: (accounts) => accounts.filter((a) => !a.isExternal),
        });

    const {
        control,
        register,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
    } = useForm<CheckDepositSchema>({
        resolver: zodResolver(checkDepositSchema),
        defaultValues: { amount: '', depositAccount: null },
    });

    const onSelectAccount = (account: DepositAccount) => {
        if (account.isExternal && account.verified !== true) {
            return;
        }

        setValue(
            'depositAccount',
            {
                id: account.id,
                nickname: account.nickname,
                bankName: account.bankName,
                accountNumberMasked: account.accountNumberMasked,
                accountNumber: account.accountNumber,
                isExternal: account.isExternal,
                imgBase64: account.imgBase64,
                logo: account.logo,
                icon: account.icon,
                color: account.color,
                verified: account.verified,
                accountType: account.accountType,
                accountNumberMaskedIsTokenized:
                    account.accountNumberMaskedIsTokenized,
            },
            { shouldValidate: true },
        );
    };

    const onSubmit = (data: CheckDepositSchema) => {
        onSubmitProp({
            amount: data.amount,
            depositAccount: data.depositAccount,
        });
    };

    return (
        <Pressable
            onPress={Keyboard.dismiss}
            style={{ flex: 1 }}
            disabled={isNonMobileWeb}
        >
            <ScreenContainer
                title="Deposit check"
                hasBackButton
                onFallbackRoute="/home/banking"
            >
                <YStack
                    height="100%"
                    gap={24}
                    justifyContent={
                        isNonMobileWeb ? 'flex-start' : 'space-between'
                    }
                >
                    <YStack>
                        <Form
                            onSubmit={handleSubmit(onSubmit)}
                            marginTop="10%"
                            marginBottom={10}
                            gap={24}
                        >
                            <Animated.View>
                                <ControlledTextInput
                                    Input={StyledH1EmphasizedInput}
                                    type="numeric"
                                    name="amount"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    height="auto"
                                    enterKeyHint="done"
                                    textAlign="center"
                                    borderWidth={0}
                                    maxWidth={FORM_MAX_WIDTH}
                                    placeholder="$0"
                                    placeholderTextColor="$black"
                                    formatValueOnChange={
                                        formatCurrencyInputOnChange
                                    }
                                    containerStyle={{
                                        alignItems: 'center',
                                        paddingHorizontal: 8,
                                    }}
                                    {...redactPrivate()}
                                />
                            </Animated.View>

                            <ControlledDepositAccountSelect
                                name="depositAccount"
                                control={control}
                                register={register}
                                errors={errors}
                                label="Deposit to"
                                placeholder="Select account"
                                onPress={() => {
                                    setIsAccountSheetOpen(true);
                                    Keyboard.dismiss();
                                }}
                            />
                        </Form>
                    </YStack>

                    <YStack gap={8}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Capture check"
                            onPress={handleSubmit(onSubmit)}
                            disabled={!isValid}
                        />
                        <CheckDepositLimitsButton />
                    </YStack>
                </YStack>
            </ScreenContainer>

            <SelectAccountSheet
                isOpen={isAccountSheetOpen}
                depositAccounts={depositAccounts}
                onClose={() => {
                    setIsAccountSheetOpen(false);
                }}
                onPlaidExit={() => {
                    setIsAccountSheetOpen(false);
                }}
                onSelectAccount={onSelectAccount}
                enableLinkingNewAccount={false}
                onNeedsReview={() => {
                    setIsReviewSheetOpen(true);
                }}
            />
            <NeedsReviewSheet
                isOpen={isReviewSheetOpen}
                onClose={() => {
                    setIsReviewSheetOpen(false);
                }}
            />
        </Pressable>
    );
};

export default CheckDepositDetails;
